import { useMutation, useQuery } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { Dialog, DialogTitle, DialogContent, DialogActions } from "@mui/material";
import { ListLayout, TableCard, InboundHeaderCard } from "ui-components/Layout";
import { FormTitle } from "ui-components/Form";
import CheckboxTable2 from "ui-components/tables/CheckboxTable2";
import { CheckboxHeader, CheckboxCell } from "ui-components/tables/components/table";
import { useCustomerValue } from "providers/CustomerContext";
import { useTeamValue } from "providers/TeamContext";
import {
  getCycleCountReport,
  saveCycleCountReportReason,
  closeCycleCountReport,
  reconsolidateCycleCountReport,
  exportCycleCountReport,
} from "services/data.inventory.service";
import { LoadingDialog, ExportDialog, Success, Error_ } from "ui-components/Alert";
import ReconsolidateList from "./reconsolidate-list";
import dayjs from "dayjs";

async function getBlobErrorMessage(err) {
  try {
    const blob = err?.response?.data;
    if (blob instanceof Blob) {
      const body = JSON.parse(await blob.text());
      if (body?.message) return body.message;
    }
  } catch (e) {
    // fall through to generic message
  }
  return "Export failed.";
}

const CLOSE_ACTION = {
  title: "Close Selected Records",
  confirmLabel: "confirm close",
  description:
    "The selected records will be marked as closed (status C). No stock is changed. This cannot be undone.",
};

function CycleCountReport() {
  const { permissions, allowAll } = useTeamValue();
  const { customerCode } = useCustomerValue();
  const [rows, setRows] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [alert, setAlert] = useState(null);
  const [isExporting, setIsExporting] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [queuedIds, setQueuedIds] = useState([]);
  const [reasons, setReasons] = useState({});

  const hasPermission = (action) =>
    allowAll ||
    (Boolean(permissions["Cycle Count Report"]) &&
      permissions["Cycle Count Report"].findIndex((b) => b === action) !== -1);
  const exportPermission = hasPermission("export");
  const editPermission = hasPermission("edit");
  const closePermission = hasPermission("close");
  const reconsolidatePermission = hasPermission("reconsolidate");

  const onError = (err) => {
    setAlert(<Error_ data={err} onChange={setAlert} />);
  };

  const { refetch, isFetching } = useQuery({
    queryKey: ["cycle-count-report", { customerCode }],
    queryFn: () => getCycleCountReport({ customerCode }),
    enabled: Boolean(customerCode),
    refetchOnMount: "always",
    onSuccess: (res) => {
      const loaded = (res.data.data || []).map((r) => ({ ...r, id: (r.stkIds || []).join("-") }));
      setRows(loaded);
      setReasons({});
      setQueuedIds((prev) => prev.filter((id) => loaded.some((r) => r.id === id)));
    },
    onError,
  });

  const saveMutation = useMutation(saveCycleCountReportReason, {
    onSuccess: (res) => setAlert(<Success data={res} onChange={setAlert} />),
    onError: (err) => {
      onError(err);
      refetch();
    },
  });

  const onSave = useCallback(
    async (row, reason) => {
      await saveMutation.mutateAsync({
        customerCode,
        skuCode: row.skuCode,
        mln: row.mln,
        expiryDate: row.expiryDate,
        reason,
      });
      setReasons((prev) => ({ ...prev, [row.rowId ?? row.id]: reason || null }));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [customerCode]
  );

  const actionMutationOptions = {
    onSuccess: (res) => {
      setCloseOpen(false);
      setQueuedIds([]);
      setSelectedRows([]);
      setAlert(<Success data={res} onChange={setAlert} />);
      refetch();
    },
    onError: (err) => {
      setCloseOpen(false);
      onError(err);
    },
  };
  const closeMutation = useMutation(closeCycleCountReport, actionMutationOptions);
  const reconsolidateMutation = useMutation(reconsolidateCycleCountReport, actionMutationOptions);
  const isActing = closeMutation.status === "loading" || reconsolidateMutation.status === "loading";

  const selectedStkIds = useMemo(() => selectedRows.flatMap((r) => r.stkIds || []), [selectedRows]);

  const onConfirmClose = () => closeMutation.mutate({ customerCode, stkIds: selectedStkIds });

  const onConfirmReconsolidate = (lines) =>
    reconsolidateMutation.mutate({
      customerCode,
      stkIds: queuedRows.flatMap((r) => r.stkIds || []),
      lines,
    });

  const availableRows = useMemo(() => rows.filter((r) => !queuedIds.includes(r.id)), [rows, queuedIds]);
  const queuedRows = useMemo(
    () =>
      rows
        .filter((r) => queuedIds.includes(r.id))
        .map((r) => (r.id in reasons ? { ...r, reason: reasons[r.id] } : r)),
    [rows, queuedIds, reasons]
  );

  const onAddToList = () => {
    setQueuedIds((prev) => [...prev, ...selectedRows.map((r) => r.id)]);
    setSelectedRows([]);
  };
  const onRemoveFromList = useCallback(
    (id) => setQueuedIds((prev) => prev.filter((x) => x !== id)),
    []
  );

  const handleSelectedRowsChange = useCallback((selected) => {
    setSelectedRows(selected);
  }, []);

  const columns = useMemo(
    () => [
      { id: "select", header: CheckboxHeader, cell: CheckboxCell },
      { header: "Cycle Count No", accessorKey: "cycleCountNo" },
      { header: "Location From", accessorKey: "fromLocation" },
      { header: "SKU", accessorKey: "skuCode" },
      {
        header: "Expiry Date",
        accessorKey: "expiryDate",
        cell: ({ row }) =>
          row.original.expiryDate ? new Date(row.original.expiryDate).toLocaleDateString("en-GB") : "",
      },
      { header: "MLN", accessorKey: "mln" },
      { header: "EXTRA", accessorKey: "extraQuantity" },
      { header: "MIA", accessorKey: "miaQuantity" },
      { header: "Unmatch Quantity", accessorKey: "unmatchQuantity" },
    ],
    []
  );

  const onExport = async (format) => {
    setAlert(null);
    setIsExporting(true);
    try {
      const res = await exportCycleCountReport({ customerCode, format });
      const isPdf = format === "pdf";
      const blob = new Blob([res.data], {
        type: isPdf
          ? "application/pdf"
          : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute(
        "download",
        `${customerCode}_${dayjs(new Date()).format("YYYYMMDD-HHmm")}_CycleCountReport.${
          isPdf ? "pdf" : "xlsx"
        }`
      );
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setAlert(<Error_ content={await getBlobErrorMessage(err)} onChange={setAlert} />);
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <ListLayout>
      {(isFetching || isActing) && <LoadingDialog />}
      {isExporting && <ExportDialog />}
      <MDBox sx={{ pt: 3 }}>
        {alert && <MDBox sx={{ mb: 3 }}>{alert}</MDBox>}
        <InboundHeaderCard>
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={1}
          >
            <FormTitle title="Cycle Count Report" />
            <MDBox display="flex" flexWrap="wrap" gap={1}>
              <MDButton
                variant="gradient"
                color="info"
                disabled={!exportPermission || rows.length === 0 || isExporting}
                onClick={() => onExport("excel")}
              >
                export excel
              </MDButton>
              <MDButton
                variant="gradient"
                color="info"
                disabled={!exportPermission || rows.length === 0 || isExporting}
                onClick={() => onExport("pdf")}
              >
                export pdf
              </MDButton>
              <MDButton
                variant="gradient"
                color="warning"
                disabled={!reconsolidatePermission || selectedRows.length === 0}
                onClick={onAddToList}
              >
                add to reconsolidate list
              </MDButton>
              <MDButton
                variant="gradient"
                color="error"
                disabled={!closePermission || selectedRows.length === 0}
                onClick={() => setCloseOpen(true)}
              >
                close
              </MDButton>
            </MDBox>
          </MDBox>
        </InboundHeaderCard>
      </MDBox>

      <TableCard sx={{ my: 3 }}>
        <CheckboxTable2
          data={availableRows}
          columns={columns}
          onSelectedRowsChange={handleSelectedRowsChange}
        />
      </TableCard>

      {queuedRows.length > 0 && (
        <ReconsolidateList
          rows={queuedRows}
          customerCode={customerCode}
          canReconsolidate={reconsolidatePermission}
          canEditReason={editPermission}
          onSaveReason={onSave}
          isSubmitting={isActing}
          onRemove={onRemoveFromList}
          onConfirm={onConfirmReconsolidate}
        />
      )}

      <Dialog open={closeOpen} onClose={() => setCloseOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{CLOSE_ACTION.title}</DialogTitle>
        <DialogContent>
          <MDTypography variant="body2" sx={{ mb: 1 }}>
            {selectedRows.length} row{selectedRows.length === 1 ? "" : "s"} selected.
          </MDTypography>
          <MDTypography variant="body2">{CLOSE_ACTION.description}</MDTypography>
        </DialogContent>
        <DialogActions>
          <MDButton variant="outlined" color="secondary" onClick={() => setCloseOpen(false)}>
            cancel
          </MDButton>
          <MDButton variant="gradient" color="error" onClick={onConfirmClose} disabled={isActing}>
            {CLOSE_ACTION.confirmLabel}
          </MDButton>
        </DialogActions>
      </Dialog>

    </ListLayout>
  );
}

export default CycleCountReport;
